/**
 * Timeline Build Entry
 *
 * @module features/releases/components/TimelineBuildEntry
 * @description A main build (push) of a pushed-image application, rendered as
 * the same "band" a release run is on a GitHub application: the same Card,
 * `TimelineNode` pass-rate ring, identity row and meta line, with the push's
 * tags where the commit SHA sits and the short push id where the commit title
 * sits. Clicking the identity or meta line selects the build in the timeline's
 * master-detail pane (`ReleaseBuildPane`). The kebab holds "Claim in <env>"
 * with the push id as the version.
 *
 * Keep the markup in step with `TimelineRunEntry` and the embedded
 * `TimelineCommitEntry`: build rows and commit bands must have the same
 * geometry.
 */

import React from 'react';
import { Card, Text, Tooltip, TooltipTrigger } from '@chatsdet/ui';
import { CheckCircle, XCircle } from '@phosphor-icons/react';
import { formatDistanceToNow } from 'date-fns';
import type { TimelineBuildEntry as BuildEntry } from '../hooks/useReleaseTimeline';
import type { BandRect } from './ReleaseRunPaneReveal';
import { ReleaseRowMenu, type ClaimTarget } from './ReleaseRowMenu';
import { SuiteRunLink } from './SuiteRunLink';
import { suiteStatusLabels } from './suiteRun';
import { TimelineNode } from './TimelineNode';
import { BandMetaLine, BandRail, bandCardClassName } from './TimelineBand';
import { buildTagsLabel, isBuildExpired } from '../buildLabels';

type Props = {
  entry: BuildEntry;
  isSelected: boolean;
  onSelect: (buildId: string, rect?: BandRect) => void;
  /** The application's sandbox environments, for the menu's Claim items; `null` when not set up for Sandboxes v2. */
  claimTarget: ClaimTarget | null;
};

const pushStatusLabels: Record<Exclude<BuildEntry['pushStatus'], 'ready'>, string> = {
  uploading: 'Uploading',
  importing: 'Importing',
  failed: 'Failed',
};

const nodeStatusByPush: Record<BuildEntry['pushStatus'], 'neutral' | 'failed' | 'active'> = {
  uploading: 'active',
  importing: 'active',
  failed: 'failed',
  ready: 'neutral',
};

export function TimelineBuildEntry({ entry, isSelected, onSelect, claimTarget }: Props) {
  const bandRef = React.useRef<HTMLDivElement>(null);
  const { suiteRun } = entry;

  const handleClick = () => {
    const rect = bandRef.current?.getBoundingClientRect();
    onSelect(entry.id, rect ? { top: rect.top, bottom: rect.bottom } : undefined);
  };

  const metaParts: React.ReactNode[] = [];
  if (entry.pushStatus === 'failed') {
    metaParts.push(
      <TooltipTrigger key="status" placement="top" delay={100} offset={6}>
        <button type="button" className="cursor-help bg-transparent p-0">
          <Text size="xs" weight="medium" className="text-red-600 cofactor-dark:text-red-400">
            {pushStatusLabels.failed}
          </Text>
        </button>
        <Tooltip variant="light" className="max-w-64 px-3 py-2 text-left">
          <Text size="xs">{entry.error ?? 'The push failed.'}</Text>
        </Tooltip>
      </TooltipTrigger>
    );
  } else if (entry.pushStatus !== 'ready') {
    metaParts.push(
      <Text key="status" size="xs" weight="medium" className="text-blue-600 cofactor-dark:text-blue-400">
        {pushStatusLabels[entry.pushStatus]}
      </Text>
    );
  }
  if (suiteRun) {
    const { failedWorkflows: failed, successfulWorkflows: passed } = suiteRun;
    const settled = failed + passed;
    const label =
      failed > 0
        ? `${failed} regression${failed !== 1 ? 's' : ''} failed`
        : passed > 0
          ? `${passed} regression${passed !== 1 ? 's' : ''} passed`
          : suiteStatusLabels[suiteRun.status];
    const colorClass = failed > 0
        ? 'text-red-600 cofactor-dark:text-red-400'
        : settled > 0
          ? 'text-green-600 cofactor-dark:text-green-400'
          : 'text-muted-foreground';
    const Icon = failed > 0 ? XCircle : CheckCircle;
    metaParts.push(
      <SuiteRunLink
        key="regression"
        suiteRun={suiteRun}
        label={label}
        colorClass={colorClass}
        icon={<Icon size={12} />}
        title="Test suite"
      />
    );
    const notRun = suiteRun.skippedWorkflows + suiteRun.degradedWorkflows;
    if (notRun > 0) {
      metaParts.push(
        <Text key="not-run" size="xs" className="text-muted-foreground">
          {notRun} skipped
        </Text>
      );
    }
  } else if (entry.pushStatus === 'ready') {
    metaParts.push(
      <Text key="no-tests" size="xs" className="text-muted-foreground">
        No tests run
      </Text>
    );
  }
  metaParts.push(
    <Text key="time" size="xs" className="text-muted-foreground">
      {formatDistanceToNow(new Date(entry.createdAt), { addSuffix: true })}
    </Text>
  );

  const expired = entry.pushStatus === 'ready' && isBuildExpired(entry);

  return (
    <div className="relative py-1 group/run">
      <BandRail />
      <Card
        ref={bandRef}
        variant="bordered"
        padding="sm"
        className={bandCardClassName(isSelected)}
        aria-current={isSelected ? 'true' : undefined}
      >
        <div className="flex items-start gap-3">
          <TimelineNode suiteRun={suiteRun} status={nodeStatusByPush[entry.pushStatus]} />

          <div className="flex-1 min-w-0">
            {/* Identity row — same structure as the embedded commit row of a band. */}
            <div className="group relative flex items-start gap-3">
              <div className="flex-1 min-w-0 pt-1">
                <div className="flex items-baseline gap-2 cursor-pointer" onClick={handleClick}>
                  <Text size="sm" weight="medium" className="font-mono text-foreground">
                    {buildTagsLabel(entry.tags)}
                  </Text>
                  <Text
                    size="sm"
                    className="flex-1 min-w-0 truncate cursor-pointer text-muted-foreground hover:text-primary-600 transition-colors"
                  >
                    push {entry.id.slice(0, 8)}
                  </Text>
                </div>
              </div>
            </div>

            <BandMetaLine parts={metaParts} onClick={handleClick} />
          </div>

          <ReleaseRowMenu
            claimVersion={
              entry.pushStatus === 'ready'
                ? {
                    version: entry.id,
                    repoFullName: null,
                    disabledReason: expired ? 'expired: image no longer retained' : undefined,
                  }
                : null
            }
            claimTarget={claimTarget}
          />
        </div>
      </Card>
    </div>
  );
}
